import { useMemo } from 'react';
import { Link as RouterLink } from 'react-router';
import {
  ArrowRight,
  CalendarPlus,
  Camera,
  ChatCircleDots,
  Clock,
  Info,
  MapPin,
  Phone,
  ShieldCheck,
  Sparkle,
  Wallet,
  Wrench,
  type Icon as PhosphorIcon,
} from '@phosphor-icons/react';
import { VStack, HStack, StackItem } from '@astryxdesign/core/Stack';
import { Heading, Text } from '@astryxdesign/core/Text';
import { Button } from '@astryxdesign/core/Button';
import { Card } from '@astryxdesign/core/Card';
import { Icon } from '@astryxdesign/core/Icon';
import { List, ListItem } from '@astryxdesign/core/List';
import { AspectRatio } from '@astryxdesign/core/AspectRatio';
import { Grid } from '@astryxdesign/core/Grid';
import { useTenant } from '@/app/tenant-context';
import { mediaUrl } from '@/lib/supabase';
import { duration, money, telHref, WEEKDAYS } from '@/lib/format';
import { Reveal } from '@/components/Reveal';
import { useShell } from './ClientShell';

const INFO_ICONS: Record<string, PhosphorIcon> = {
  shield: ShieldCheck,
  clock: Clock,
  wallet: Wallet,
  wrench: Wrench,
  camera: Camera,
  sparkle: Sparkle,
  info: Info,
};

export function HomePage() {
  const { tenant } = useTenant();
  const { openBooking, openAssistant } = useShell();
  const hero = tenant.photos.find((p) => p.kind === 'hero');
  const works = tenant.photos.filter((p) => p.kind === 'work');
  const popular = useMemo(() => {
    const p = tenant.services.filter((s) => s.is_popular);
    return (p.length ? p : tenant.services).slice(0, 4);
  }, [tenant.services]);

  return (
    <VStack gap={10}>
      {/* Hero: the glass CTA sits inside the photo frame and scrolls with it. */}
      <header className="hero-frame">
        {hero && (
          <img
            src={mediaUrl(hero.url)}
            alt={`${tenant.name}: автомобиль в студии`}
            className="absolute inset-0 -z-20 size-full object-cover"
            fetchPriority="high"
            decoding="async"
          />
        )}
        <span aria-hidden className="hero-shade absolute inset-0 -z-10" />
        <VStack gap={4} height="100%" vAlign="end" paddingInline={4} paddingBlockEnd={6} paddingBlockStart={10}>
          <VStack gap={2}>
            <Heading level={1} type="display-2" textWrap="balance">
              {tenant.name}
            </Heading>
            {tenant.tagline && (
              <Text type="large" color="secondary" textWrap="pretty">
                {tenant.tagline}
              </Text>
            )}
          </VStack>
          <button
            type="button"
            onClick={() => openBooking()}
            className="glass group relative flex min-h-14 w-full items-center justify-center gap-2 rounded-full px-6 text-primary transition-transform duration-200 active:scale-[0.98] focus-visible:outline-2"
          >
            <span className="glass-label flex items-center gap-2 text-lg font-semibold">
              <Icon icon={CalendarPlus} size="md" color="inherit" />
              Записаться
            </span>
          </button>
        </VStack>
      </header>

      <VStack gap={10} paddingInline={4}>
        {tenant.description && (
          <Reveal label="О студии">
            <Text color="secondary" as="p" textWrap="pretty">
              {tenant.description}
            </Text>
          </Reveal>
        )}

        <Reveal label="Запись в студию">
          <VStack gap={3}>
            <Heading level={2}>Запись в студию</Heading>
            <Card padding={4} elevation="low">
              <VStack gap={4}>
                <Text color="secondary">Выберите услугу и свободное время — подтверждение сразу.</Text>
                <List density="balanced" hasDividers>
                  {popular.map((s) => (
                    <ListItem
                      key={s.id}
                      label={s.name}
                      description={duration(s.duration_minutes)}
                      endContent={
                        <Text weight="semibold" hasTabularNumbers>
                          {money(s.price_cents, tenant.currency, s.price_is_from)}
                        </Text>
                      }
                      onClick={() => openBooking(s.id)}
                    />
                  ))}
                </List>
                <Button label="Выбрать время" variant="primary" size="lg" width="100%" onClick={() => openBooking()} />
              </VStack>
            </Card>
          </VStack>
        </Reveal>

        {tenant.assistant_enabled && (
          <Reveal label="Помощник">
            <Card padding={4} variant="muted">
              <VStack gap={3}>
                <HStack gap={2} vAlign="center">
                  <Icon icon={ChatCircleDots} size="md" color="accent" />
                  <Heading level={3}>Спросите помощника</Heading>
                </HStack>
                <HStack gap={2} wrap="wrap">
                  {(tenant.assistant_suggestions.length
                    ? tenant.assistant_suggestions
                    : ['Когда ближайшее окно?', 'Сколько стоит полировка?', 'Как найти студию?']
                  ).map((q) => (
                    <Button key={q} label={q} size="sm" variant="secondary" onClick={() => openAssistant(q)} />
                  ))}
                </HStack>
                <Button label="Задать свой вопрос" variant="ghost" onClick={() => openAssistant()} />
              </VStack>
            </Card>
          </Reveal>
        )}

        <Reveal label="Услуги и цены">
          <VStack gap={3}>
            <HStack vAlign="center">
              <StackItem size="fill">
                <Heading level={2}>Услуги и цены</Heading>
              </StackItem>
              <RouterLink to="/services" className="inline-flex min-h-11 items-center gap-1 text-accent">
                Все <Icon icon={ArrowRight} size="sm" color="inherit" />
              </RouterLink>
            </HStack>
            <List density="balanced" hasDividers>
              {tenant.services.slice(0, 6).map((s) => (
                <ListItem
                  key={s.id}
                  label={s.name}
                  description={`${s.category ? `${s.category} · ` : ''}${duration(s.duration_minutes)}`}
                  endContent={
                    <Text weight="semibold" hasTabularNumbers>
                      {money(s.price_cents, tenant.currency, s.price_is_from)}
                    </Text>
                  }
                  onClick={() => openBooking(s.id)}
                />
              ))}
            </List>
          </VStack>
        </Reveal>

        {works.length > 0 && (
          <Reveal label="Наши работы">
            <VStack gap={3}>
              <Heading level={2}>Наши работы</Heading>
              <ul className="scroll-snap-x -mx-4 flex scroll-px-4 gap-3 overflow-x-auto px-4 pb-1" aria-label="Фото работ">
                {works.map((w) => (
                  <li key={w.id} className="w-[78%] max-w-sm shrink-0">
                    <VStack gap={2}>
                      <AspectRatio ratio={3 / 2}>
                        <img
                          src={mediaUrl(w.url)}
                          alt={w.caption || 'Пример работы'}
                          loading="lazy"
                          decoding="async"
                          className="size-full rounded-lg object-cover"
                        />
                      </AspectRatio>
                      {w.caption && (
                        <Text type="supporting" maxLines={1}>
                          {w.caption}
                        </Text>
                      )}
                    </VStack>
                  </li>
                ))}
              </ul>
            </VStack>
          </Reveal>
        )}

        {tenant.info_cards.length > 0 && (
          <Reveal label="Почему мы">
            <Grid columns={{ minWidth: 240, repeat: 'fit' }} gap={3}>
              {tenant.info_cards.map((c) => (
                <Card key={c.position} padding={4}>
                  <VStack gap={2}>
                    <Icon icon={INFO_ICONS[c.icon] ?? Info} size="lg" color="accent" />
                    <Heading level={3}>{c.title}</Heading>
                    {c.body && <Text color="secondary">{c.body}</Text>}
                  </VStack>
                </Card>
              ))}
            </Grid>
          </Reveal>
        )}

        <Reveal label="Контакты">
          <VStack gap={3}>
            <Heading level={2}>Как нас найти</Heading>
            <Card padding={4}>
              <VStack gap={4}>
                <HStack gap={3} vAlign="start">
                  <Icon icon={MapPin} size="md" color="accent" />
                  <StackItem size="fill">
                    <Text>{tenant.address}</Text>
                  </StackItem>
                </HStack>
                <WorkingHours />
                <HStack gap={2} wrap="wrap">
                  <Button label="Позвонить" icon={<Icon icon={Phone} />} href={telHref(tenant.phone)} />
                  {tenant.map_url && (
                    <Button
                      label="Маршрут"
                      icon={<Icon icon={MapPin} />}
                      href={tenant.map_url}
                      target="_blank"
                      rel="noopener noreferrer"
                    />
                  )}
                </HStack>
              </VStack>
            </Card>
          </VStack>
        </Reveal>
      </VStack>
    </VStack>
  );
}

function WorkingHours() {
  const { tenant } = useTenant();
  const rows = WEEKDAYS.map((label, i) => {
    const intervals = tenant.hours.filter((h) => h.weekday === i + 1);
    return { label, value: intervals.length ? intervals.map((h) => `${h.opens}–${h.closes}`).join(', ') : 'выходной' };
  });
  return (
    <HStack gap={3} vAlign="start">
      <Icon icon={Clock} size="md" color="accent" />
      <StackItem size="fill">
        <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1">
          {rows.map((r) => (
            <Row key={r.label} label={r.label} value={r.value} />
          ))}
        </dl>
      </StackItem>
    </HStack>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <>
      <dt>
        <Text color="secondary">{label}</Text>
      </dt>
      <dd>
        <Text hasTabularNumbers color={value === 'выходной' ? 'secondary' : 'primary'}>
          {value}
        </Text>
      </dd>
    </>
  );
}
